import { green, red } from '@mui/material/colors'
import { isNil } from 'lodash'

import { PERCENT_CHANGE_THRESHOLD } from '@/constants'
import { formatCurrency } from '@/i18n/tolgee'
import { DashboardStat, ProductDashboardStat } from '@/mocks/mockSummaryStats'

export const getColorForStat = (change: number, positiveIsBad: boolean) => {
  if (positiveIsBad) {
    return change > 0 ? red[500] : green[500]
  } else {
    return change < 0 ? red[500] : green[500]
  }
}

export function formatAmount(
  stat: DashboardStat | ProductDashboardStat,
): string {
  if (stat.value == null) return ''
  switch (stat.valueType) {
    case 'PERCENTAGE':
      return `${Math.round(stat.value * 100)}%`
    case 'RATIO':
      return `${Math.round(stat.value)} / ${stat.totalValue}`
    case 'CURRENCY':
      return formatCurrency(stat.value)
    case 'SINGLE_VALUE':
    default:
      return `${Math.round(stat.value).toLocaleString()}`
  }
}

export function computePercentChange(
  stat: DashboardStat | ProductDashboardStat,
): { text: string; color: string } | null {
  const { value, previousValue, totalValue, previousTotalValue, valueType } =
    stat
  if (previousValue == null) return null

  const useRatio = valueType === 'RATIO' && totalValue && previousTotalValue

  const curr =
    useRatio && !isNil(value) && !isNil(totalValue)
      ? value / totalValue
      : value ?? 0

  const prev =
    useRatio && previousValue !== undefined && previousTotalValue !== undefined
      ? previousValue / previousTotalValue
      : previousValue ?? 0

  // Check if prev is 0 or undefined
  if (prev === 0 || prev === undefined) return null

  const diff = curr - prev
  // For percentage values, calculate difference in percentage points; otherwise use standard percentage change formula
  const pct = valueType === 'PERCENTAGE' ? diff * 100 : (diff / prev) * 100
  const rounded = Math.round(pct * 100) / 100

  if (Math.abs(rounded) > PERCENT_CHANGE_THRESHOLD) return null

  const sign = rounded > 0 ? '+' : ''
  // TODO: Add a way to determine if the stat is positive or negative not hardcoded
  const POSITIVE_IS_BAD = stat.label.includes('Empty')
  const color = getColorForStat(rounded, POSITIVE_IS_BAD)

  return { text: `${sign}${rounded}%`, color }
}
